import { useEffect, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { message } from 'antd';
import {
  CheckOutlined,
  CloseOutlined,
  TrophyOutlined,
  LoadingOutlined,
  StopOutlined,
  UserDeleteOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { telegram } from '../../../utils/telegram';
import { useBookingsStore } from '../../../store/bookingsStore';
import ConfirmActionDialog from './ConfirmActionDialog';
import NoShowReasonDialog from './NoShowReasonDialog';
import DeclineReasonDialog from './DeclineReasonDialog';
import type { Booking, NoShowReason, BookingDeclineReason } from '../../../types/provider';

interface Props {
  booking: Booking;
  onActionChange?: (open: boolean) => void;
}

type DialogAction = 'cancel' | 'complete' | null;

// ─── Custom action button ────────────────────────────────────────────────────

type ActionVariant = 'primary' | 'success' | 'danger';

interface ActionButtonProps {
  variant: ActionVariant;
  icon: ReactNode;
  label: string;
  loading?: boolean;
  disabled?: boolean;
  fullWidth?: boolean;
  onClick: () => void;
}

const VARIANT_STYLES: Record<ActionVariant, CSSProperties> = {
  primary: {
    background: 'linear-gradient(135deg, #3390ec 0%, #1677ff 100%)',
    color: '#fff',
    border: '1px solid rgba(22,119,255,0.15)',
    boxShadow: '0 2px 8px rgba(22,119,255,0.25), inset 0 1px 0 rgba(255,255,255,0.18)',
  },
  success: {
    background: 'linear-gradient(135deg, #1f8b4c 0%, #27ae60 100%)',
    color: '#fff',
    border: '1px solid rgba(31,139,76,0.15)',
    boxShadow: '0 2px 8px rgba(31,139,76,0.28), inset 0 1px 0 rgba(255,255,255,0.18)',
  },
  danger: {
    background: 'rgba(255,77,79,0.06)',
    color: '#d93b3e',
    border: '1px solid rgba(255,77,79,0.22)',
    boxShadow: 'none',
  },
};

function ActionButton({
  variant,
  icon,
  label,
  loading = false,
  disabled = false,
  fullWidth = false,
  onClick,
}: ActionButtonProps) {
  const [isPressed, setIsPressed] = useState(false);
  const base = VARIANT_STYLES[variant];
  const isInactive = disabled || loading;

  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        if (!isInactive) onClick();
      }}
      disabled={isInactive}
      onTouchStart={() => !isInactive && setIsPressed(true)}
      onTouchEnd={() => setIsPressed(false)}
      onTouchCancel={() => setIsPressed(false)}
      onMouseDown={() => !isInactive && setIsPressed(true)}
      onMouseUp={() => setIsPressed(false)}
      onMouseLeave={() => setIsPressed(false)}
      style={{
        ...base,
        flex: fullWidth ? '1 1 100%' : '1 1 0',
        minHeight: 38,
        padding: '0 14px',
        borderRadius: 10,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 6,
        fontSize: 13,
        fontWeight: 600,
        cursor: isInactive ? 'not-allowed' : 'pointer',
        opacity: isInactive ? 0.55 : 1,
        transform: isPressed && !isInactive ? 'scale(0.97)' : 'scale(1)',
        transition: 'transform 0.1s ease, opacity 0.15s ease, box-shadow 0.15s ease',
        WebkitTapHighlightColor: 'transparent',
        letterSpacing: 0.1,
        outline: 'none',
        userSelect: 'none',
      }}
    >
      <span style={{ display: 'inline-flex', fontSize: 13, lineHeight: 1 }}>
        {loading ? <LoadingOutlined spin /> : icon}
      </span>
      <span>{label}</span>
    </button>
  );
}

// ─── Main component ─────────────────────────────────────────────────────────

export default function BookingQuickActions({ booking, onActionChange }: Props) {
  const { t } = useTranslation('provider');
  const {
    confirmBooking,
    declineBooking,
    completeBooking,
    cancelByProvider,
    markNoShow,
    actionBookingId,
  } = useBookingsStore();
  const [dialogAction, setDialogAction] = useState<DialogAction>(null);
  // Keep last action's content visible during drawer close animation
  const [lastDialogAction, setLastDialogAction] = useState<NonNullable<DialogAction>>('cancel');
  const [noShowOpen, setNoShowOpen] = useState(false);
  const [declineOpen, setDeclineOpen] = useState(false);

  useEffect(() => {
    if (dialogAction) setLastDialogAction(dialogAction);
  }, [dialogAction]);

  const isActing = actionBookingId === booking.id;

  // Service vaqti boshlangan: Bekor qilish o'rnida Bajarildi/Bajarilmadi ko'rinadi
  const isServiceStarted =
    booking.status === 'CONFIRMED' &&
    new Date(booking.startTime).getTime() <= Date.now();

  useEffect(() => {
    onActionChange?.(dialogAction !== null || noShowOpen || declineOpen);
  }, [dialogAction, noShowOpen, declineOpen, onActionChange]);

  const handleConfirm = async () => {
    telegram.hapticFeedback.impact('light');
    await confirmBooking(booking.id);
    telegram.hapticFeedback.notification('success');
  };

  const openDialog = (action: DialogAction) => {
    setDialogAction(action);
  };

  const closeDialog = () => {
    setDialogAction(null);
  };

  const openNoShow = () => {
    telegram.hapticFeedback.impact('medium');
    setNoShowOpen(true);
  };

  const closeNoShow = () => {
    if (isActing) return;
    setNoShowOpen(false);
  };

  const openDecline = () => {
    telegram.hapticFeedback.impact('medium');
    setDeclineOpen(true);
  };

  const closeDecline = () => {
    if (isActing) return;
    setDeclineOpen(false);
  };

  const handleDialogConfirm = async () => {
    if (!dialogAction) return;
    telegram.hapticFeedback.impact('medium');
    if (dialogAction === 'complete') {
      const result = await completeBooking(booking.id);
      closeDialog();
      if (result.ok) {
        telegram.hapticFeedback.notification('success');
        void message.success(t('bookings.actions.completeSuccess'));
      } else {
        telegram.hapticFeedback.notification('error');
        if (result.kind !== 'conflict') void message.error(result.message);
      }
      return;
    }
    if (dialogAction === 'cancel') await cancelByProvider(booking.id);
    telegram.hapticFeedback.notification('success');
    closeDialog();
  };

  const handleDeclinePick = async (reason: BookingDeclineReason) => {
    await declineBooking(booking.id, reason);
    setDeclineOpen(false);
    telegram.hapticFeedback.notification('success');
  };

  const handleNoShowPick = async (reason: NoShowReason) => {
    const result = await markNoShow(booking.id, reason);
    setNoShowOpen(false);
    if (result.ok) {
      telegram.hapticFeedback.notification('success');
      void message.success(t('bookings.actions.noShowSuccess'));
    } else {
      telegram.hapticFeedback.notification('error');
      if (result.kind !== 'conflict') void message.error(result.message);
    }
  };

  const dialogConfig: Record<
    NonNullable<DialogAction>,
    { title: string; description: string; confirmText: string; danger: boolean }
  > = {
    cancel: {
      title: t('bookings.actions.cancelDialogTitle'),
      description: t('bookings.actions.cancelDialogDesc'),
      confirmText: t('bookings.actions.cancelDialogConfirm'),
      danger: true,
    },
    complete: {
      title: t('bookings.actions.completeDialogTitle'),
      description: t('bookings.actions.completeDialogDesc'),
      confirmText: t('bookings.actions.completeDialogConfirm'),
      danger: false,
    },
  };

  const { status } = booking;

  // Determine action set
  const showPendingActions = status === 'PENDING';
  const showCancelOnly = status === 'CONFIRMED' && !isServiceStarted;
  const showCompletionActions = status === 'CONFIRMED' && isServiceStarted;
  const hasAnyAction = showPendingActions || showCancelOnly || showCompletionActions;

  if (!hasAnyAction) {
    return null;
  }

  return (
    <>
      <div
        style={{
          display: 'flex',
          gap: 8,
          marginTop: 12,
          paddingTop: 10,
          borderTop: '1px dashed var(--bl-divider, #ebebeb)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {showPendingActions && (
          <>
            <ActionButton
              variant="primary"
              icon={<CheckOutlined />}
              label={t('bookings.actions.confirm')}
              loading={isActing}
              disabled={isActing}
              onClick={handleConfirm}
            />
            <ActionButton
              variant="danger"
              icon={<CloseOutlined />}
              label={t('bookings.actions.decline')}
              disabled={isActing}
              onClick={openDecline}
            />
          </>
        )}

        {showCancelOnly && (
          <ActionButton
            variant="danger"
            icon={<StopOutlined />}
            label={t('bookings.actions.cancel')}
            disabled={isActing}
            fullWidth
            onClick={() => openDialog('cancel')}
          />
        )}

        {showCompletionActions && (
          <>
            <ActionButton
              variant="success"
              icon={<TrophyOutlined />}
              label={t('bookings.actions.complete')}
              loading={isActing}
              disabled={isActing}
              onClick={() => openDialog('complete')}
            />
            <ActionButton
              variant="danger"
              icon={<UserDeleteOutlined />}
              label={t('bookings.actions.noShow')}
              disabled={isActing}
              onClick={openNoShow}
            />
          </>
        )}
      </div>

      <ConfirmActionDialog
        open={dialogAction !== null}
        title={dialogConfig[lastDialogAction].title}
        description={dialogConfig[lastDialogAction].description}
        confirmText={dialogConfig[lastDialogAction].confirmText}
        danger={dialogConfig[lastDialogAction].danger}
        loading={isActing}
        onConfirm={handleDialogConfirm}
        onCancel={closeDialog}
      />

      <NoShowReasonDialog
        open={noShowOpen}
        loading={isActing}
        onPick={(r) => void handleNoShowPick(r)}
        onCancel={closeNoShow}
      />

      <DeclineReasonDialog
        open={declineOpen}
        loading={isActing}
        onPick={(r) => void handleDeclinePick(r)}
        onCancel={closeDecline}
      />
    </>
  );
}
